import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	EDashboardActions,
	GetAvailableShifts,
	GetAvailableShiftsSuccess,
	GetDashboardActionsCount,
	GetDashboardActionsCountSuccess,
	GetDashboardUserSalaryCalculationSuccess,
	GetDashboardUserSalaryCalculation,
	GetNextShifts,
	GetNextShiftsSuccess,
	GetPunchSettings,
	GetPunchSettingsSuccess,
	GetPunchStatus,
	GetPunchStatusSuccess,
	GetWeatherData,
	GetWeatherDataSuccess,
	OpenPunchPopup,
	PunchIn,
	PunchInSuccess,
	PunchOut,
	PunchOutSuccess
} from '@app/core/store/actions/dashboard.actions';
import {DashboardService} from '@app/core/services/api/dashboard/dashboard.service';
import {Shift} from '@app/core/models/shift.model';
import {IUserTotalCalcWithDepartment} from '@app/core/interfaces/salary';
import {SnackBar} from '@app/core/services/snackbar.service';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {PunchPopupComponent} from '@app/modules/home/modules/dashboard/components/punch-popup/punch-popup.component';
import {IPunchSettings, IPunchStatus, IWeather} from '@app/core/store/state/dashboard.state';
import {ShiftService} from '@app/modules/shared/services/shift.service';
import {AuthService} from '@app/core/services/auth.service';
import {Action} from '@ngrx/store';
import {GetShifts} from '@app/core/store/actions/schedule.actions';
import * as moment from 'moment';

@Injectable()
export class DashboardEffects {

	@Effect()
	public onGetDashboardNextShifts$ = this.actions$.pipe(
		ofType<GetNextShifts>(EDashboardActions.GetNextShifts),
		switchMap((action: GetNextShifts) => from(this.dashboardService.getNextShifts(action.pageSize)).pipe(
			switchMap((shifts: Shift[]) => [
				new GetNextShiftsSuccess(shifts),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetDashboardActionsCount$ = this.actions$.pipe(
		ofType<GetDashboardActionsCount>(EDashboardActions.GetDashboardActionsCount),
		switchMap((action: GetDashboardActionsCount) => from(this.dashboardService.getActionsCount()).pipe(
			switchMap((count: any) => [
				new GetDashboardActionsCountSuccess(count),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetDashboardUserSalaryCalculation$ = this.actions$.pipe(
		ofType<GetDashboardUserSalaryCalculation>(EDashboardActions.GetDashboardUserSalaryCalculation),
		switchMap((action: GetDashboardUserSalaryCalculation) => from(this.dashboardService.getDashboardUserSalaryCalculation(action.userId, action.year, action.month)).pipe(
			switchMap((salaryCalc: IUserTotalCalcWithDepartment) => [
				new GetDashboardUserSalaryCalculationSuccess(salaryCalc),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetPunchSettings$ = this.actions$.pipe(
		ofType<GetPunchSettings>(EDashboardActions.GetPunchSettings),
		switchMap((action: GetPunchSettings) => from(this.dashboardService.getPunchSettings()).pipe(
			switchMap((settings: IPunchSettings) => [
				new GetPunchSettingsSuccess(settings),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPunchIn$ = this.actions$.pipe(
		ofType<PunchIn>(EDashboardActions.PunchIn),
		switchMap((action: PunchIn) => from(this.dashboardService.punchStart(action.data)).pipe(
			switchMap((punchData: any) => {
				this.dialog.closeAll();

				const actions: Action[] = this.authService.currentUser.isAdmin() ?
					[
						new PunchInSuccess(punchData),
						new GetDashboardActionsCount(),
						new GetShifts(moment(), moment()),
						new GetNextShifts(3),
					]
					: [
						new PunchInSuccess(punchData),
						new GetNextShifts(3),
					];

				return actions;
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPunchOut$ = this.actions$.pipe(
		ofType<PunchOut>(EDashboardActions.PunchOut),
		switchMap((action: PunchOut) => from(this.dashboardService.punchEnd(action.data)).pipe(
			switchMap((punchData: any) => {
				this.dialog.closeAll();
				const actions: Action[] = this.authService.currentUser.isAdmin() ?
					[
						new PunchOutSuccess(punchData),
						new GetDashboardActionsCount(),
						new GetShifts(moment(), moment()),
						new GetNextShifts(3),
					] :
					[
						new PunchOutSuccess(punchData),
						new GetNextShifts(3),
					];

				return actions;
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onOpenPunchModal$ = this.actions$.pipe(
		ofType<OpenPunchPopup>(EDashboardActions.OpenPunchPopup),
		switchMap((action: OpenPunchPopup) => {
			if (!(action.data.groups?.length > 0)) {
				this.snackBar.newError(this.translate.instant('SCHEDULE.NO_GROUPS_IN_DEPARTMENT'));

				return [];
			}
			this.dialog.open(PunchPopupComponent, {
				restoreFocus: false,
				id: 'punchUserModal',
				data: action.data,
				panelClass: 'fullscreen-dialog-max-s',
			});

			return [];
		}),
	);

	@Effect()
	public onGetPunchStatus$ = this.actions$.pipe(
		ofType<GetPunchStatus>(EDashboardActions.GetPunchStatus),
		switchMap((action: GetPunchStatus) => from(this.dashboardService.getPunchStatus()).pipe(
			switchMap((status: IPunchStatus) => [
				new GetPunchStatusSuccess(status),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetAvailableShifts$ = this.actions$.pipe(
		ofType<GetAvailableShifts>(EDashboardActions.GetAvailableShifts),
		switchMap((action: GetAvailableShifts) => from(this.shiftsService.getAvailableShifts(action.pageSize)).pipe(
			switchMap((shifts: Shift[]) => [
				new GetAvailableShiftsSuccess(shifts),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetWeatherData$ = this.actions$.pipe(
		ofType<GetWeatherData>(EDashboardActions.GetWeatherData),
		switchMap((action: GetWeatherData) => from(this.dashboardService.getWeatherData(action.postal_code, action.country_code)).pipe(
			switchMap((data: IWeather[]) => [
				new GetWeatherDataSuccess(data),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private actions$: Actions,
		private dashboardService: DashboardService,
		private shiftsService: ShiftService,
		private snackBar: SnackBar,
		private dialog: MatDialog,
		private translate: TranslateService,
		private authService: AuthService,
	) {
	}
}
